import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, ChevronDown, Clock, Send } from "lucide-react";
import { accessRequestCancel, accessRequestCreate, accessRequestMine, errorMessage } from "./api";
import { card, h2, field } from "./ui";

const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "");

// "Ask HikmahVerses for access": for a free account that would rather not add a card for the trial (or has used it).
// Shows the right thing for where the request stands: the form, "waiting", "approved until ...", or why it ended.
// part: "status" shows only a pending / approved request, "form" shows only the request form (folded away when `framed`), omitted shows whichever applies.
export default function AccessRequestPanel({ token, onChanged, framed = false, part }) {
  const [m, setM] = useState(null);
  const [plan, setPlan] = useState("adult");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => accessRequestMine(token).then(setM).catch(() => setM({ failed: true })), [token]);
  useEffect(() => { load(); }, [load]);
  if (!m) return <div className="text-sm text-foreground/65">Loading…</div>;
  if (m.failed) return null;
  const r = m.request;
  const planName = (id) => (m.plans.find((p) => p.id === id) || {}).name || id;
  const shell = (el) => (framed ? <div className={`${card} space-y-2`} data-testid="plans-access">{el}</div> : el);
  const send = async () => {
    setBusy(true);
    try { await accessRequestCreate(token, plan, message.trim() || null); setMessage(""); toast.success("Request sent. We'll email you when there's an answer."); await load(); } catch (e) { toast.error(errorMessage(e, "Couldn't send the request")); } finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm("Withdraw your request?")) return;
    try { await accessRequestCancel(token); toast.success("Request withdrawn"); await load(); } catch (e) { toast.error(errorMessage(e)); }
  };

  const hasStatus = !!r && (r.state === "pending" || r.state === "approved");
  if ((part === "status" && !hasStatus) || (part === "form" && hasStatus)) return null;
  if (r && r.state === "pending") {
    return shell(
      <div className="space-y-2 text-sm" data-testid="access-pending">
        <div className="flex items-center gap-2 font-semibold text-forest-700"><Clock className="h-4 w-4" />Your request for {planName(r.requested_plan)} is waiting for approval</div>
        <p className="text-foreground/70">Sent {day(r.created_at)}. We&apos;ll email you as soon as there&apos;s an answer; you don&apos;t need to do anything else.</p>
        {r.message && <p className="rounded-xl bg-sand-100 p-3 text-foreground/80">&ldquo;{r.message}&rdquo;</p>}
        <button type="button" className="text-forest-700 underline" onClick={cancel} data-testid="access-cancel">Withdraw request</button>
      </div>,
    );
  }
  if (r && r.state === "approved") {
    return shell(
      <div className="space-y-1 text-sm" data-testid="access-approved">
        <div className="flex items-center gap-2 font-semibold text-forest-700"><Check className="h-4 w-4" />You have {planName(r.granted_plan)} access, granted by HikmahVerses</div>
        <p className="text-foreground/70">{r.expires_at ? `It lasts until ${day(r.expires_at)}.` : "It has no end date."}{r.review_note ? ` Note from our team: ${r.review_note}` : ""}</p>
      </div>
    );
  }
  if (!m.can_request || part === "status") return null;
  const ended = r && { declined: "Your last request wasn't approved", revoked: "Your earlier access has ended", expired: "Your earlier access has ended" }[r.state];
  const form = (
    <div className="space-y-3 text-sm" data-testid="access-form">
      {ended && <p className="rounded-xl bg-sand-100 p-3 text-foreground/80" data-testid="access-last">{ended}{r.state === "expired" && r.expires_at ? ` on ${day(r.expires_at)}` : ""}.{r.review_note ? ` Note from our team: ${r.review_note}` : ""}</p>}
      <p className="text-foreground/70">No card needed. Tell us which plan you&apos;d like and why, and our team will review it. If it&apos;s approved, the plan is unlocked on your account for the time we choose.</p>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Plan">
        {m.plans.map((p) => (
          <button key={p.id} type="button" role="radio" aria-checked={plan === p.id} data-testid={`access-plan-${p.id}`} onClick={() => setPlan(p.id)}
            className={`rounded-full border px-4 py-1.5 ${plan === p.id ? "bg-forest-700 text-sand-100 border-forest-700" : "bg-white border-sand-300 text-forest-700"}`}>{p.name}</button>))}
      </div>
      <textarea className={`${field} h-20`} placeholder="Why would you like access? (optional)" maxLength={500} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="access-message" />
      <button type="button" className="btn-primary disabled:opacity-60" disabled={busy} onClick={send} data-testid="access-send">{busy ? "Sending…" : "Send request"} <Send className="h-4 w-4" /></button>
    </div>
  );
  if (!framed) return form;
  return (
    <details className={`${card} group`} data-testid="plans-access" open={!!ended}>
      <summary className="cursor-pointer list-none flex items-center justify-between gap-3">
        <span className={`${h2} text-base`}>Prefer not to add a card? Ask us for access</span>
        <ChevronDown className="h-4 w-4 text-forest-700 transition-transform group-open:rotate-180" aria-hidden="true" />
      </summary>
      <div className="mt-3">{form}</div>
    </details>
  );
}
